import React, { useEffect } from 'react'
import { useT } from '@transifex/react'
import AntdSpin from 'antd/es/spin'

import { Spinner } from 'src/components/ui/spinner'
import type { DesignMode } from 'src/design-mode/store'
import { useDesignMode } from 'src/design-mode/useDesignMode'
import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * THE PATH THE SEAM CANNOT REACH.
 *
 * `../Spin` switches every `<Spin>` the APP renders — all 135 tags. It cannot
 * switch the ones antd renders for itself, because those are `antd/es/spin`
 * imported from inside the package, three modules deep, with no seam in
 * between. In antd 5.15 exactly four components do that
 * (`grep -rl "from '../spin'" node_modules/antd/es`): **Table**, **List**,
 * `Mentions` and `QRCode`. This repo renders the first two — Table from 238
 * JSX tags across 91 files, and every one of the kit tables goes through
 * `storybook/components/table-kit/createTableUIStatefulColumns.tsx`, which
 * passes `loading` straight to antd.
 *
 * So with the design toggle ON, a table refetch — a page change, a filter, a
 * poll — still painted antd's four blue dots over the rows while the rest of
 * the app had moved to the spoked spinner. Browser-confirmed on
 * `/app/tickets`: `.ant-spin-dot` with four `.ant-spin-dot-item` children at
 * `rgb(24, 144, 255)`. That is the loader the captain reported as "also there
 * sometimes"; "sometimes" is "whenever a list reloads".
 *
 * THE ONLY LEVER antd 5.15 GIVES. `ConfigProvider` gained a
 * `spin={{ indicator }}` slot in 5.20; on 5.15 its `spin` entry is a
 * `ComponentStyleConfig` (className/style only). The single app-wide hook is
 * the module-level `Spin.setDefaultIndicator`, which antd's own
 * `renderIndicator` consults BEFORE falling through to the four dots — and
 * which every internal Spin reads, since they all import the same module. So
 * this component installs the app's spinner there while the toggle is on.
 *
 * WHY THIS IS SAFE WITH THE TOGGLE OFF, which is the whole premise of the
 * design track. Nothing is written unless the mode is `'shadcn'`: the effect
 * returns early, so a session that has never flipped the toggle never calls
 * `setDefaultIndicator` at all and antd's `defaultIndicator` stays the `null`
 * it is initialised to. The cleanup restores `null` — and only ever runs if
 * this component installed something first.
 *
 * WHAT antd DOES TO THE ELEMENT, and why it is a WRAPPER rather than a bare
 * `<Spinner />`. `renderIndicator` clones the default indicator and appends
 * `ant-spin-dot` to its className. That class is pure geometry —
 * `font-size: <dotSize>; width: 1em; height: 1em` at 14 / 20 / 32px for
 * small / default / large, plus the `position: absolute; top: 50%; left: 50%`
 * centring antd uses in the nested pattern. Letting it land on a wrapper is
 * what makes the spinner track a `<Table size="small">` for free. The wrapper
 * is also the colour PARENT the glyph needs: `.anticon { color: inherit }` in
 * the icon seam's unlayered runtime sheet outranks any colour utility on the
 * glyph itself, so the colour has to be set one level up — the same rule
 * `src/components/ui/spinner.tsx` records. `.ant-spin-dot` declares no
 * `color`, so `text-muted-foreground` here is contested by nothing: it simply
 * beats the `color: <colorPrimary>` the spinner would otherwise INHERIT from
 * antd's `.ant-spin`.
 *
 * `text-[1em]` on the Spinner replaces its own `text-base`, so the glyph takes
 * the size antd just put on the wrapper instead of pinning itself at 16px.
 * Rotation stays SINGLE-SPEED: the seam puts `loadingCircle 1s linear
 * infinite` on the glyph's `<svg>` and the wrapper adds none — the clone gets
 * `ant-spin-dot`, never `ant-spin-dot-spin`, which is the class carrying
 * antd's own rotation.
 *
 * THE STATIC `antd/es/spin` IMPORT IS DELIBERATE, and it is the LEAF, never
 * the `src/antd` barrel (PRO-130): this module is reached from
 * `src/App/index.tsx`, so the barrel's `export * from 'antd'` would weld
 * antd's whole component surface onto the entry chunk. The leaf costs the Spin
 * component and its cssinjs style module, on top of the antd theme runtime
 * `src/App/AntdI18n.tsx` already puts there. A dynamic import would cost
 * nothing until the toggle is flipped, but it cannot deliver the indicator
 * before the first Table renders — see the note on the component below, where
 * that ordering is the entire point.
 * ------------------------------------------------------------------------- */

/**
 * The element antd clones. A component rather than a literal element so the
 * accessible name can be translated at render time; `className` is the prop
 * antd's `cloneElement` writes `ant-spin-dot` into, so it has to be accepted
 * and forwarded.
 */
const AntdSpinIndicator = ({ className }: { className?: string }) => {
  const t = useT()

  return (
    <span
      data-slot="spin-indicator"
      className={cn(
        'inline-flex items-center justify-center text-muted-foreground',
        className
      )}
      // antd's `.ant-spin-dot` is unlayered and sets `display: inline-block`,
      // which outranks the utility above however specific it is. Inline is the
      // one declaration that beats it, and centring the glyph in the box antd
      // sized is the whole job of this element.
      style={{ display: 'inline-flex', lineHeight: 0 }}
    >
      <Spinner className="text-[1em]" label={t('Loading')} />
    </span>
  )
}

const INDICATOR = <AntdSpinIndicator />

/**
 * Whether THIS module is the one that wrote antd's default indicator. antd
 * initialises `defaultIndicator` to `null` and nothing else in this repo calls
 * `setDefaultIndicator`, so tracking our own write is what lets the antd path
 * stay untouched: with the toggle never flipped this module writes nothing at
 * all, and the restore below cannot clobber a value it did not install.
 *
 * It writes `AntdSpin.setDefaultIndicator` DIRECTLY rather than the seam's
 * `Spin.setDefaultIndicator` (`../Spin`), which writes BOTH halves: the seam
 * setter also sets the shadcn Spin's indicator, overriding its own per-size
 * fallback (`SIZE_CLASS` small/default/large), so the seam's Spins would stop
 * tracking their size. Accepted consequence: two writers share one antd global
 * with no coordination between them, so a future caller of the seam setter is
 * a review point rather than a surprise.
 */
let installed = false

/**
 * How many `<AntdSpinDefaultIndicator />`s are currently mounted. The app
 * mounts exactly one, at the App root, but a boolean cannot express "someone
 * still needs this": with two mounted, the first to unmount would otherwise
 * clear the indicator the other one still wants. antd's indicator is released
 * only when the LAST one goes.
 */
let mountedInstances = 0

/**
 * Idempotent. Calling it with the mode already in force is a no-op, which is
 * what makes it safe to call from a render body (below) — including twice, as
 * StrictMode's double render does.
 */
const syncAntdDefaultIndicator = (designMode: DesignMode) => {
  const wanted = designMode === 'shadcn'
  if (wanted === installed) return

  installed = wanted
  AntdSpin.setDefaultIndicator(wanted ? INDICATOR : null)
}

/**
 * Installs the app's spinner as antd's app-wide default indicator while the
 * design toggle is on, so the Spins antd renders INSIDE Table and List stop
 * falling through to the four blue dots.
 *
 * Renders nothing. Mounted once, at the App root beside `<AppFont />`
 * (`src/App/index.tsx`) — the same shape as the font dispatcher, and for the
 * same reason: this is a global the seams cannot express one call site at a
 * time.
 *
 * THE WRITE HAPPENS DURING RENDER, NOT IN AN EFFECT, and that is the whole
 * reason this component has a body at all. antd reads `defaultIndicator`
 * while a Spin RENDERS, and antd's internal Spins subscribe to nothing — so an
 * indicator installed in an effect arrives one commit too late for every Table
 * that rendered in the same pass, and that Table keeps the four dots until
 * something unrelated re-renders it. Measured, not reasoned: with the install
 * in a `useEffect` a Table mounted alongside this component painted 4
 * `.ant-spin-dot-item`s and never dropped them. This component sits above the
 * whole tree, so a render-phase write is ordered before every consumer's
 * render by construction. It is allowed to be a write because
 * `syncAntdDefaultIndicator` is idempotent and external-store-shaped: a repeat
 * render, a StrictMode double render and a discarded render all produce the
 * same single assignment.
 *
 * The effect RE-ASSERTS the install rather than only cleaning up, which is
 * what makes the two halves symmetric. A mount lifecycle that runs a cleanup
 * without a following render — StrictMode's dev double-mount is render,
 * render, effect, cleanup, effect — would otherwise leave antd's
 * `defaultIndicator` at `null` with the toggle still on, and every internal
 * Spin back on the four dots. The re-assert is a no-op in the normal case
 * because the write is idempotent.
 *
 * The unmount restore keeps the module honest for tests, which mount and
 * unmount this in both modes inside one process — and it is refcounted, so it
 * only fires when the last instance goes.
 */
export const AntdSpinDefaultIndicator = () => {
  const designMode = useDesignMode()

  syncAntdDefaultIndicator(designMode)

  useEffect(() => {
    mountedInstances += 1
    syncAntdDefaultIndicator(designMode)

    return () => {
      mountedInstances -= 1
      if (mountedInstances === 0) syncAntdDefaultIndicator('antd')
    }
  }, [designMode])

  return null
}

export default AntdSpinDefaultIndicator
